<script setup>
// 翻转图片工具（flip-image）。复刻 design "Flip Image.html" 内联 <script> 的 flip 逻辑：
// 上传 → canvas 预览 → Flip horizontal / Flip vertical（canvas translate + scale(-1,1)/(1,-1)）→ Apply & Download。
// 纯浏览器（Canvas + Image + FileReader + URL.createObjectURL），文件不上传。复刻原 flip(h) 数学：
//   cx.translate(h?c.width:0, h?0:c.height); cx.scale(h?-1:1, h?1:-1); cx.drawImage(work,0,0);
import { ref, computed, nextTick, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useAuthModals } from "@/composables/useAuthModals";
import { usePaywallModal } from "@/composables/usePaywallModal";
import { useBatch } from "@/composables/useBatch.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";
import { tierOf, fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, default: "flip-image" }, // 单工具，mode 备用
  slug: { type: String, default: "" },
});

const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

const store = useStore();

const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileName = ref("image.png");
const fileInput = ref(null);
const canvas = ref(null); // 显示用 canvas
const thumbUrl = ref("");

// 源图绘制到离屏 work canvas（保持原始像素，所有翻转基于它，可叠加）
let work = null;
let baseName = "image";
let srcType = "image/png";
let lastUrl = "";

const dimReadout = computed(() => {
  if (!work) return "—";
  return work.width + " × " + work.height + " px";
});

function fmtType() {
  return srcType === "image/jpeg" ? "image/jpeg" : srcType === "image/webp" ? "image/webp" : "image/png";
}

function paint() {
  const c = canvas.value;
  if (!c || !work) return;
  c.width = work.width;
  c.height = work.height;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.drawImage(work, 0, 0);
}

function revoke() {
  if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; }
}

async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  // 浏览器解不了的源(heic/tiff/tga/dds/psd…)先经后端转 png,再进 canvas(用户无感)。
  // 命名/baseName 仍取自原始 file(dds 编辑后下载名沿用用户文件名),只有 canvas 源 blob 换成 _f。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  srcType = file.type;
  fileName.value = file.name;
  revoke();
  const url = URL.createObjectURL(_f);
  lastUrl = url;
  const im = new Image();
  im.onload = async () => {
    work = document.createElement("canvas");
    work.width = im.naturalWidth;
    work.height = im.naturalHeight;
    work.getContext("2d").drawImage(im, 0, 0);
    thumbUrl.value = url;
    stage.value = "work";
    await nextTick(); // canvas 在 v-if 里,等挂载后再绘制
    paint();
  };
  im.onerror = () => { alert("Could not load this image."); };
  im.src = url;
}

/* ---------- 批量模式(nuxt#21):多文件 → 队列。纯客户端 flip。 ---------- */
const batchMode = ref(false);
const tierB = computed(() => tierOf(!!store.state.user.token, store.state.user.userInfo));
const tokenB = computed(() => store.state.user.token || "");
const userIdB = computed(() => (store.state.user.userInfo && store.state.user.userInfo.user_info_id) || "");
const _auth = useAuthModals();
const _paywall = usePaywallModal();

// 批量共享翻转设置：一套设置应用到全部文件（与单文件即时翻转不同，批量用布尔开关描述要套的镜像）。
const flipH = ref(false); // 水平镜像
const flipV = ref(false); // 垂直镜像

const batch = useBatch({
  category: "image",
  client: true,
  tier: tierB,
  token: tokenB,
  userId: userIdB,
  clientConvert: (file) => convertFileBatch(file),
  buildQuery: () => "",
  onSignup: () => _auth.openSignInModal(),
  onUpgrade: () => _paywall.openPaywall(["DST", "PES", "EXP", "JEF", "VP3", "HUS", "XXX", "EMD"]),
});

// 镜像单文件 flip(h) 的 canvas 数学，但输入任意 file、按共享 flipH/flipV 套用（可同时水平+垂直）。
// 不套任何翻转时退回原图字节。产物格式跟随源类型（png/jpg/webp）。
async function convertFileBatch(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = () => rej(new Error("Could not read image"));
      im.src = url;
    });
    const W = image.naturalWidth, H = image.naturalHeight;
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const cx = c.getContext("2d");
    // 复刻 flip(h)：水平镜像 translate(W,0)+scale(-1,1)；垂直镜像 translate(0,H)+scale(1,-1)；二者可叠加。
    cx.translate(flipH.value ? W : 0, flipV.value ? H : 0);
    cx.scale(flipH.value ? -1 : 1, flipV.value ? -1 : 1);
    cx.drawImage(image, 0, 0);
    const tt = (file.type === "image/jpeg" || file.type === "image/webp") ? file.type : "image/png";
    const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
    const blob = await new Promise((r) => c.toBlob(r, tt, 0.95));
    if (!blob) throw new Error("Conversion failed");
    return { blob, ext };
  } finally { URL.revokeObjectURL(url); }
}

function enterBatch(files) {
  batchMode.value = true;
  batch.clearAll();
  batch.addFiles(files);
}
function addMoreFiles() { if (fileInput.value) fileInput.value.click(); }
// 套餐变化 → 重算锁定；队列清空 → 退出批量回到 dropzone。
watch([tierB], () => batch.reevaluate());
watch(() => batch.items.value.length, (n) => {
  if (batchMode.value && n === 0) batchMode.value = false;
});

/* ---- 拖放 / 选择 ---- */
function pick() { fileInput.value && fileInput.value.click(); }
function handleFiles(files) {
  if (!files || !files.length) return;
  if (batchMode.value) { batch.addFiles(files); return; } // 已在批量 → add more
  if (files.length > 1) { enterBatch(files); return; }     // 多文件 → 进批量
  loadFile(files[0]);                                       // 单文件 → 原有单文件流
}
function onInput(e) { handleFiles(e.target.files); e.target.value = ""; }
function onDrop(e) { e.preventDefault(); dragging.value = false; handleFiles(e.dataTransfer && e.dataTransfer.files); }

function reset() {
  stage.value = "drop";
  work = null;
  thumbUrl.value = "";
  if (fileInput.value) fileInput.value.value = "";
}

/* ---- 翻转（复刻原 flip(h)）：h=true 水平镜像，h=false 垂直镜像 ---- */
function flip(h) {
  if (!work) return;
  const c = document.createElement("canvas");
  c.width = work.width;
  c.height = work.height;
  const cx = c.getContext("2d");
  cx.translate(h ? c.width : 0, h ? 0 : c.height);
  cx.scale(h ? -1 : 1, h ? 1 : -1);
  cx.drawImage(work, 0, 0);
  work = c; // 回填，便于继续叠加翻转 / 再次下载（复刻原 work=c）
  paint();
}

/* ---- 下载（复刻原 downloadBtn） ---- */
function download() {
  if (!work) return;
  const tt = fmtType();
  const ext = tt === "image/jpeg" ? "jpg" : tt === "image/webp" ? "webp" : "png";
  work.toBlob((b) => downloadBlob(b, baseName + "-flipped." + ext), tt, 0.95);
}

onBeforeUnmount(revoke);
</script>

<template>
  <div class="flip-tool">
    <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop' && !batchMode" class="dropzone" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M7 8l-4 4 4 4M17 8l4 4-4 4" /></svg></div>
      <h3>Drop your image here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
      <div class="dz-or">Flipped right in your browser — never stored</div>
      <button class="btn btn-amber dz-btn" type="button" @click.stop="pick">Choose Image</button>
    </div>

    <!-- batch 多文件队列（nuxt#21）：拖入 >1 个文件时进入；共享翻转设置走 #params 插槽 -->
    <div v-if="batchMode" class="workspace show">
      <BatchQueue :batch="batch" category="image" :client="true" :file-count-max="20" @add-more="addMoreFiles">
        <template #params>
          <div class="ctl">
            <label>Flip</label>
            <div class="xform-grid">
              <button type="button" :class="{ active: flipH }" @click="flipH = !flipH"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M12 3v18M7 8l-4 4 4 4M17 8l4 4-4 4" /></svg>Flip horizontal</button>
              <button type="button" :class="{ active: flipV }" @click="flipV = !flipV"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 12h18M8 7l4-4 4 4M8 17l4 4 4-4" /></svg>Flip vertical</button>
            </div>
          </div>
          <div style="font-size:12.5px;color:var(--tx-faint);font-weight:600">One set of settings applies to all files.</div>
        </template>
      </BatchQueue>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work' && !batchMode" class="workspace show">
      <div class="ws-grid">
        <div class="ws-preview">
          <div class="cropwrap">
            <canvas ref="canvas"></canvas>
          </div>
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <img class="thumb" :src="thumbUrl" alt="" />
            <div class="meta">
              <div class="fn">{{ fileName }}</div>
              <div class="fsz">{{ dimReadout }}</div>
            </div>
            <span class="rm" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
          </div>

          <div class="ctl">
            <label>Flip</label>
            <div class="xform-grid">
              <button type="button" @click="flip(true)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M12 3v18M7 8l-4 4 4 4M17 8l4 4-4 4" /></svg>Flip horizontal</button>
              <button type="button" @click="flip(false)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 12h18M8 7l4-4 4 4M8 17l4 4 4-4" /></svg>Flip vertical</button>
            </div>
          </div>

          <div class="dim-readout">{{ dimReadout }}</div>

          <div class="ws-actions">
            <button class="btn btn-amber btn-lg" type="button" @click="download">Apply &amp; Download</button>
            <button class="btn btn-outline" type="button" @click="reset">Flip another image</button>
          </div>
        </div>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live canvas preview</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
    </div>
  </div>
</template>

<style scoped>
.flip-tool { width: 100%; }
.ws-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.ws-preview { position: relative; background: repeating-conic-gradient(#0c0e13 0 25%, #0a0b10 0 50%) 50% / 26px 26px; min-height: 340px; display: grid; place-items: center; padding: 24px; overflow: hidden; }
.cropwrap { position: relative; display: inline-block; line-height: 0; max-width: 100%; }
.cropwrap canvas { max-height: 380px; width: auto; max-width: 100%; border-radius: 8px; box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85); }
.ws-panel { border-left: 1px solid var(--line); padding: 26px; display: flex; flex-direction: column; gap: 20px; }
.ws-file { display: flex; align-items: center; gap: 13px; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
.ws-file .thumb { width: 46px; height: 46px; border-radius: 11px; object-fit: cover; flex: none; background: var(--surface-2); }
.ws-file .meta { min-width: 0; flex: 1; }
.ws-file .fn { font-weight: 700; font-size: 14.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-file .fsz { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.ws-file .rm { color: var(--tx-faint); cursor: pointer; flex: none; padding: 6px; border-radius: 8px; transition: 0.15s; }
.ws-file .rm:hover { color: var(--c-image); background: rgba(255, 255, 255, 0.05); }
.ctl { display: flex; flex-direction: column; gap: 9px; }
.ctl label { font-size: 13px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.02em; }
.xform-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.xform-grid button { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 18px; border-radius: 12px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.xform-grid button:hover { border-color: var(--c-free); background: color-mix(in oklab, var(--c-free) 10%, var(--surface-2)); transform: translateY(-2px); }
.xform-grid button.active { border-color: var(--c-free); background: color-mix(in oklab, var(--c-free) 18%, var(--surface-2)); }
.xform-grid button svg { width: 24px; height: 24px; color: var(--c-free); }
.dim-readout { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; text-align: center; }
.ws-actions { margin-top: auto; display: flex; flex-direction: column; gap: 10px; }
.ws-actions .btn { white-space: normal; text-align: center; line-height: 1.25; }
.trust { display: flex; align-items: center; justify-content: center; gap: 30px; flex-wrap: wrap; margin: 30px auto 0; color: var(--tx-faint); font-size: 13.5px; font-weight: 600; }
.trust .i { display: flex; align-items: center; gap: 8px; }
.trust .i svg { width: 16px; height: 16px; color: var(--c-gif); }
@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } .ws-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
